<template>
  <dialog ref="dialog" :class="['modal-dialog', screenClass]" @close="handleClose">
    <div v-if="isLoading" class="w-full h-full absolute bg-curtain-color z-[100]">
      <div
        v-if="isLoading"
        class="w-[50px] h-[50px] absolute top-[50%] left-[50%] translate-y-[-50%] translate-x-[-50%] bg-spinner bg-contain"
      ></div>
    </div>
    <div :class="['dialog-content-container', screenClass]">
      <button
        v-if="showXCloseButton"
        type="button"
        class="close-button absolute top-5 right-5 h-6 w-6 focus:outline-dotted focus:outline-offset-2 focus:outline-2 focus:outline-racq-navy focus:rounded-sm focus:border-none z-10"
        aria-label="Close"
        @click.stop.prevent="close"
        @keydown.enter="close"
        @keydown.space="close"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <rect width="24" height="24" transform="translate(0 0.5)" fill="white" />
          <path
            d="M13.2699 12.5L18.7366 7.0423C19.0878 6.69106 19.0878 6.12367 18.7366 5.77243C18.3853 5.4212 17.8179 5.4212 17.4667 5.77243L12.009 11.2301L6.5423 5.76343C6.19106 5.41219 5.61467 5.41219 5.26343 5.76343C4.91219 6.11467 4.91219 6.69106 5.26343 7.0423L10.7211 12.5L5.26343 17.9577C4.91219 18.3089 4.91219 18.8763 5.26343 19.2276C5.44355 19.4077 5.6687 19.4887 5.90286 19.4887C6.13702 19.4887 6.36217 19.3987 6.5423 19.2276L12 13.7699L17.4577 19.2276C17.6378 19.4077 17.863 19.4887 18.0971 19.4887C18.3313 19.4887 18.5564 19.3987 18.7366 19.2276C19.0878 18.8763 19.0878 18.3089 18.7366 17.9577L13.2789 12.5H13.2699Z"
            fill="#B1B1B1"
          />
        </svg>
      </button>
      <!-- The content from the parent will be rendered here -->
      <slot>[Placeholder slot to fill in the modal dialog contents]</slot>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed, provide } from 'vue';

const escapeKey: string = 'Escape';
const keydown = 'keydown' as const;

const props = defineProps<{
  showXCloseButton: boolean;
  closeOnEsc: boolean;
  modelValue: boolean;
  size?: 'sm' | 'md' | 'lg' | 'tooltip';
  isLoading?: boolean;
}>();

const screenClass = computed(() => {
  // Size default to sm
  let screenClass = 'md:racq-max-w-xl md:!max-h-[620px]';
  if (props.size) {
    switch (props.size) {
      case 'md':
        screenClass = 'md:racq-max-w-xl3 md:!max-h-[620px]';
        break;
      case 'lg':
        screenClass = 'md:racq-max-w-xl4 md:!max-h-[656px]';
        break;
      case 'tooltip':
        screenClass = 'md:max-w-[696px] md:!max-h-[656px]';
        break;
      default:
        break;
    }
  }
  return screenClass;
});

const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>();

const emitUpdateModelValue = (emitValue: boolean) => {
  emit('update:modelValue', emitValue);
};

const dialog = ref<HTMLDialogElement | null>(null);

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === escapeKey && !props.closeOnEsc) {
    event.preventDefault();
  }
};

const open = async () => {
  if (dialog.value) {
    scroll_lock();
    dialog.value.showModal();
    if (!props.closeOnEsc) {
      dialog.value.addEventListener(keydown, handleEscapeKey);
    }
    await nextTick(() => {
      dialog.value?.focus();
    });
  }
};

const close = () => {
  if (dialog.value) {
    scroll_lock(false);
    dialog.value.removeEventListener(keydown, handleEscapeKey);
    dialog.value.close();
    emitUpdateModelValue(false);
  }
};

provide('dialogControl', { open, close });

const handleClose = () => {
  emitUpdateModelValue(false);
};

// The CSS class btn-livechat when added to an anchor link, is wired to open the chat window.
// The dialog overlay prevents user from interacting with the chat window.
// This click event delegate would make sure to close the dialog before opening the chat window.
const onDelegatedChatLinkClick = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target.closest('.btn-livechat')) {
    close();
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      open();
    } else {
      close();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  if (props.modelValue) {
    open();
  }
  dialog.value?.addEventListener('click', onDelegatedChatLinkClick);
});

onBeforeUnmount(() => {
  if (dialog.value) {
    close(); // call to this so the body top, left, right are back to normal
    dialog.value.removeEventListener(keydown, handleEscapeKey);
    dialog.value.removeEventListener('click', onDelegatedChatLinkClick);
  }
});

/**
 * Lock or unlock the body scroll.
 *
 * Source: https://github.com/whatwg/html/issues/7732#issuecomment-2330373381
 * @param {boolean} lock Whether to lock or unlock the body scroll. Default = true.
 */
const scroll_lock = (lock = true) => {
  const { documentElement, body } = document;

  // RTL <body> scrollbar
  const documentLeft = documentElement.getBoundingClientRect().left;
  const scrollbarX = Math.round(documentLeft) + documentElement.scrollLeft;
  const paddingProp = scrollbarX ? 'paddingLeft' : 'paddingRight';

  if (lock) {
    body.style[paddingProp] = `${window.innerWidth - documentElement.clientWidth}px`;
    body.style.top = `-${window.scrollY}px`;
    body.style.left = `-${window.scrollX}px`;
    body.style.right = '0';
    body.style.position = 'fixed';

    body.classList.add('scroll-locked');
  } else {
    if (!body.classList.contains('scroll-locked')) {
      return;
    }

    const currentScrollY = parseInt(body.style.top || '0') * -1;
    const currentScrollX = parseInt(body.style.left || '0') * -1;

    body.style[paddingProp] = '';
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';

    body.classList.remove('scroll-locked');

    window.scrollTo(currentScrollX, currentScrollY);
  }
};

defineExpose({ close, open });
</script>
<!-- external styles defined under dialog.css -->
